import React, { useRef, useState } from "react";
import { supabase } from "../supabase";
import { toast } from "react-hot-toast";
import { UploadCloud, Trash2, Link2, Loader2, ImageOff } from "lucide-react";

/* =============================================================================
   ImageUploadField
   -----------------------------------------------------------------------------
   Campo de imagen reutilizable para contenido que NO es de productos
   (banner de la tienda, logo, favicon, slides del carrusel, foto de "Nosotros").

   - Sube el archivo al bucket publico `products` bajo `{orgId}/store/...`.
     Se reutiliza ese bucket a proposito: sus policies exigen que la primera
     carpeta del path sea el organization_id (user_belongs_to_org), asi que el
     aislamiento multi-empresa ya esta cubierto y no hace falta bucket nuevo.
   - Guarda en el estado la URL publica completa (compatible con los valores
     historicos, que ya eran URLs pegadas a mano).
   - Mantiene la opcion de pegar una URL externa para no romper tiendas que ya
     tenian imagenes hosteadas afuera.
   ============================================================================= */

const STORE_ASSETS_BUCKET = "products";
const DEFAULT_MAX_SIZE_MB = 5;

const ASPECT_CLASSES = {
    banner: "aspect-[16/6]",
    wide: "aspect-[16/9]",
    square: "aspect-square",
    logo: "aspect-[3/2]",
};

const sanitizeExtension = (fileName = "") => {
    const ext = String(fileName).split(".").pop()?.toLowerCase() || "";
    return /^[a-z0-9]{1,5}$/.test(ext) ? ext : "jpg";
};

export default function ImageUploadField({
    value,
    onChange,
    organizationId,
    label,
    hint,
    folder = "store",
    prefix = "img",
    aspect = "banner",
    accept = "image/*",
    maxSizeMb = DEFAULT_MAX_SIZE_MB,
    disabled = false,
    compact = false,
}) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [showUrlInput, setShowUrlInput] = useState(false);
    const [brokenImage, setBrokenImage] = useState(false);

    const hasValue = Boolean((value || "").trim());

    const handleFile = async (event) => {
        const file = event.target.files?.[0];
        if (inputRef.current) inputRef.current.value = "";
        if (!file) return;

        if (!organizationId) {
            toast.error("No se pudo identificar la empresa. Recargá la página.");
            return;
        }
        if (!file.type?.startsWith("image/")) {
            toast.error("El archivo tiene que ser una imagen.");
            return;
        }
        if (file.size > maxSizeMb * 1024 * 1024) {
            toast.error(`La imagen supera los ${maxSizeMb} MB. Comprimila e intentá de nuevo.`);
            return;
        }

        setUploading(true);
        try {
            const ext = sanitizeExtension(file.name);
            const unique = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
            const filePath = `${organizationId}/${folder}/${prefix}-${unique}.${ext}`;

            const { error: upErr } = await supabase.storage
                .from(STORE_ASSETS_BUCKET)
                .upload(filePath, file, { cacheControl: "3600", upsert: true, contentType: file.type });
            if (upErr) throw upErr;

            const { data } = supabase.storage.from(STORE_ASSETS_BUCKET).getPublicUrl(filePath);
            const publicUrl = data?.publicUrl || "";
            if (!publicUrl) throw new Error("No se pudo obtener la URL pública de la imagen.");

            setBrokenImage(false);
            onChange(publicUrl);
            toast.success("Imagen cargada. Acordate de guardar los cambios.");
        } catch (err) {
            console.error("[ImageUploadField] upload", err);
            const message = /bucket/i.test(err?.message || "")
                ? 'No existe el bucket de Storage "products". Creálo en Supabase para poder subir imágenes.'
                : err?.message || "No se pudo subir la imagen.";
            toast.error(message);
        } finally {
            setUploading(false);
        }
    };

    const clear = () => {
        setBrokenImage(false);
        onChange("");
    };

    const previewClass = ASPECT_CLASSES[aspect] || ASPECT_CLASSES.banner;

    return (
        <div className="space-y-2">
            {label && (
                <div className="flex items-center justify-between gap-2">
                    <label className="block text-[10px] font-bold uppercase text-slate-400">{label}</label>
                    <button
                        type="button"
                        onClick={() => setShowUrlInput((prev) => !prev)}
                        className="text-[10px] font-bold uppercase tracking-wide text-slate-400 hover:text-slate-700 flex items-center gap-1"
                    >
                        <Link2 size={11} /> {showUrlInput ? "Ocultar URL" : "Usar URL"}
                    </button>
                </div>
            )}

            <div
                className={`relative w-full ${compact ? "h-24" : previewClass} rounded-xl border-2 border-dashed transition-colors overflow-hidden ${
                    disabled
                        ? "border-slate-100 bg-slate-50 cursor-not-allowed"
                        : "border-slate-200 bg-slate-50 hover:border-slate-400 cursor-pointer"
                }`}
            >
                {hasValue && !brokenImage ? (
                    <img
                        src={value}
                        alt={label || "Imagen"}
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={() => setBrokenImage(true)}
                    />
                ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 gap-1 px-3 text-center">
                        {brokenImage ? (
                            <>
                                <ImageOff size={compact ? 16 : 22} />
                                <span className="text-[10px] font-bold uppercase tracking-wide">No se pudo cargar</span>
                            </>
                        ) : (
                            <>
                                <UploadCloud size={compact ? 16 : 22} />
                                <span className="text-[10px] font-bold uppercase tracking-wide">Subir imagen</span>
                                {!compact && (
                                    <span className="text-[9px] text-slate-400">JPG, PNG o WEBP · hasta {maxSizeMb} MB</span>
                                )}
                            </>
                        )}
                    </div>
                )}

                {uploading && (
                    <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center gap-2 text-slate-700">
                        <Loader2 size={16} className="animate-spin" />
                        <span className="text-[10px] font-black uppercase tracking-widest">Subiendo…</span>
                    </div>
                )}

                <input
                    ref={inputRef}
                    type="file"
                    accept={accept}
                    onChange={handleFile}
                    disabled={disabled || uploading}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                />
            </div>

            <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] text-slate-400 leading-tight">{hint}</span>
                {hasValue && !disabled && (
                    <button
                        type="button"
                        onClick={clear}
                        className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-red-500 hover:text-red-700 flex items-center gap-1"
                    >
                        <Trash2 size={11} /> Quitar
                    </button>
                )}
            </div>

            {showUrlInput && (
                <input
                    value={value || ""}
                    onChange={(e) => {
                        setBrokenImage(false);
                        onChange(e.target.value);
                    }}
                    disabled={disabled}
                    placeholder="https://... (URL de imagen externa)"
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs font-mono focus:ring-2 focus:ring-slate-900 outline-none"
                />
            )}
        </div>
    );
}
